'use client';
import { useState, useRef, useEffect } from 'react';
import { isBrowser } from '@s7/platform';
import { randomId } from '@s7/utils';
import { useIsomorphicEffect } from '../useIsomorphicEffect/useIsomorphicEffect.js';

var IS_BROWSER = isBrowser();
var createClass = function(name, content) {
    var style = document.createElement("style");
    style.innerHTML = "." + name + " { " + content + " }";
    document.getElementsByTagName("head")[0].appendChild(style);
    return function() {
        return style.remove();
    };
};
/**
 * Хук, позволяющий управлять блокировкой скролла страницы. Полезен при создании модальных окон и других интерфейсных элементов, которые требуют временной блокировки прокрутки.
 *
 * @param autoLock - Если `true`, скролл будет заблокирован автоматически при монтировании компонента. По-умолчанию `true`
 *
 * @returns Объект с состоянием блокировки и функциями для управления блокировкой
 *
 * @primaryExample
 * // Пример использования хука для блокировки скролла при открытии модального окна
 * const ModalComponent = () => {
 *   useLockBodyScroll();
 *
 *   return <div>Modal Content</div>;
 * };
 *
 * @example
 * // Пример использования хука с ручным управлением блокировкой скролла
 * const AnotherComponent = () => {
 *   const { isLocked, lock, unlock } = useLockBodyScroll(false);
 *
 *   const toggleLock = () => {
 *     if (isLocked) {
 *       unlock();
 *     } else {
 *       lock();
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={toggleLock}>
 *         {isLocked ? 'Разблокировать скролл' : 'Заблокировать скролл'}
 *       </button>
 *     </div>
 *   );
 * };
 */ function useLockBodyScroll(autoLock) {
    if (autoLock === void 0) autoLock = true;
    var _useState = useState(false), isLocked = _useState[0], setIsLocked = _useState[1];
    var id = useRef("ds-sclock-" + randomId());
    var lock = function() {
        document.body.classList.add(id.current);
        setIsLocked(true);
    };
    var unlock = function() {
        document.body.classList.remove(id.current);
        setIsLocked(false);
    };
    useEffect(function() {
        if (!IS_BROWSER || !isLocked) {
            return;
        }
        var currentPaddingRight = parseInt(window.getComputedStyle(document.body).paddingRight, 10) || 0;
        var scrollbarWidth = window.innerWidth - document.body.scrollWidth;
        return createClass(id.current, "overflow: hidden !important; padding-right: " + (scrollbarWidth + currentPaddingRight) + "px !important");
    }, [
        isLocked
    ]);
    useIsomorphicEffect(function() {
        if (!IS_BROWSER) {
            return;
        }
        if (autoLock) {
            lock();
        }
        return unlock;
    }, [
        autoLock
    ]);
    return {
        isLocked: isLocked,
        lock: lock,
        unlock: unlock
    };
}

export { useLockBodyScroll };
